export const MEDIA_CHUNK_SIZE = 3 * 1024 * 1024

export interface MediaChunkDescriptor {
  number: number
  checksum: string
  size: number
}

export interface MediaManifest {
  contentType: string
  size: number
  checksum: string
  chunks: MediaChunkDescriptor[]
}

export async function sha256Hex(blob: Blob) {
  const hash = await crypto.subtle.digest('SHA-256', await blob.arrayBuffer())
  return [...new Uint8Array(hash)].map((value) => value.toString(16).padStart(2, '0')).join('')
}

export async function sha256Base64(blob: Blob) {
  const hash = new Uint8Array(await crypto.subtle.digest('SHA-256', await blob.arrayBuffer()))
  let binary = ''
  hash.forEach((value) => { binary += String.fromCharCode(value) })
  return btoa(binary)
}

export async function blobToBase64(blob: Blob) {
  const bytes = new Uint8Array(await blob.arrayBuffer())
  let binary = ''
  const blockSize = 32 * 1024
  for (let offset = 0; offset < bytes.length; offset += blockSize) {
    binary += String.fromCharCode(...bytes.subarray(offset, offset + blockSize))
  }
  return btoa(binary)
}

export function base64ToBytes(value: string) {
  const binary = atob(value)
  const bytes = new Uint8Array(binary.length)
  for (let index = 0; index < binary.length; index += 1) {
    bytes[index] = binary.charCodeAt(index)
  }
  return bytes
}

export function pendingChunkNumbers(
  fileSize: number,
  completed: Array<{ PartNumber: number }>,
) {
  const completedNumbers = new Set(completed.map((part) => part.PartNumber))
  const chunkCount = Math.max(1, Math.ceil(fileSize / MEDIA_CHUNK_SIZE))
  return Array.from({ length: chunkCount }, (_, index) => index + 1)
    .filter((partNumber) => !completedNumbers.has(partNumber))
}

export async function reconstructVerifiedMedia(
  manifest: MediaManifest,
  chunks: Array<{ number: number; checksum: string; data: string }>,
) {
  const parts: ArrayBuffer[] = []
  const sortedDescriptors = [...manifest.chunks].sort((a, b) => a.number - b.number)
  for (const expected of sortedDescriptors) {
    const chunk = chunks.find((item) => item.number === expected.number)
    if (!chunk) throw new Error(`Evidence chunk ${expected.number} is missing.`)
    const bytes = base64ToBytes(chunk.data)
    const checksum = await sha256Base64(new Blob([bytes]))
    if (
      bytes.byteLength !== expected.size ||
      checksum !== expected.checksum ||
      checksum !== chunk.checksum
    ) {
      throw new Error(`Evidence chunk ${expected.number} failed integrity validation.`)
    }
    parts.push(bytes.buffer.slice(bytes.byteOffset, bytes.byteOffset + bytes.byteLength) as ArrayBuffer)
  }
  const blob = new Blob(parts, { type: manifest.contentType })
  if (blob.size !== manifest.size || await sha256Hex(blob) !== manifest.checksum) {
    throw new Error('The downloaded evidence failed integrity validation.')
  }
  return blob
}
